import { memo, useEffect, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { UIMessage } from "ai";
// Relative (not "@/lib/…") so the render test resolves under the root vitest
// alias — see feedback_cf_src_vitest_alias.
import { cn } from "../lib/utils";
import { collectCitations } from "../lib/citations";
import { SourcesStrip } from "./SourcesStrip";
import { ReasoningBlock, ToolCallCard, type ToolPart } from "./MessageParts";

interface Props {
  messages: UIMessage[];
  isStreaming?: boolean;
  onExamplePrompt?: (text: string) => void;
  /** Example prompts for the empty state (themed); falls back to the general set. */
  examplePrompts?: string[];
}

const EXAMPLE_PROMPTS = [
  "What are the top drug targets for melanoma? Cross-reference Open Targets, ChEMBL, and DGIdb.",
  "For BRAF V600E in melanoma, cross-reference CIViC for therapies, Open Targets for disease links, and Reactome for the MAPK pathway context.",
  "Show me the protein structure of KRAS — pull the UniProt entry and any PDB structures.",
  "Find recent PubMed papers on GLP-1 receptor agonists in oncology — surface the most cited.",
  "Map EGFR's relationship neighborhood across Open Targets, CIViC, and Reactome — keep each link tied to its source.",
  "Build a profile for osimertinib: targets from ChEMBL plus clinical trials and phases from ClinicalTrials.gov.",
  "Run a mini systematic review on GLP-1 agonists in Alzheimer's across PubMed and ClinicalTrials.gov, with a summary table and citations.",
];

export function MessageList({
  messages,
  isStreaming,
  onExamplePrompt,
  examplePrompts,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming]);

  if (messages.length === 0) {
    return (
      <div
        data-testid="empty-state"
        className="flex flex-1 items-center justify-center px-4"
      >
        <div className="max-w-xl text-center">
          <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              className="h-7 w-7"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.847-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-1">Bio MCP Portal</h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Scientific databases across genomics, pharma, and clinical research —
            pick a domain in the header, or just ask a question.
          </p>
          <div
            className="grid grid-cols-1 gap-2 text-left sm:grid-cols-2"
            data-testid="example-prompts"
          >
            {(examplePrompts && examplePrompts.length > 0 ? examplePrompts : EXAMPLE_PROMPTS).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => onExamplePrompt?.(p)}
                data-testid="example-prompt"
                className="rounded-lg border border-border bg-card p-3 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show typing indicator if user just sent and we haven't started streaming
  // assistant text yet (or are between tool calls).
  const lastMessage = messages[messages.length - 1];
  const showTypingIndicator =
    isStreaming &&
    (!lastMessage ||
      lastMessage.role === "user" ||
      (lastMessage.role === "assistant" &&
        (lastMessage.parts || []).every(
          (p) => p.type !== "text" || !(p as { text?: string }).text,
        )));

  return (
    <div className="flex-1 overflow-y-auto p-4" data-testid="message-list">
      <div className="mx-auto max-w-3xl space-y-4">
        {messages.map((m, i) => (
          <Message
            key={m.id || i}
            message={m}
            isLast={i === messages.length - 1}
            isStreaming={!!isStreaming && i === messages.length - 1}
          />
        ))}
        {showTypingIndicator && <TypingIndicator />}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div data-testid="typing-indicator" className="flex justify-start">
      <div className="rounded-2xl bg-muted px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/60" />
        </div>
      </div>
    </div>
  );
}

// Memoized so a streaming turn only re-renders the message whose parts are
// actually growing (the last one) — not every prior message + its dozens of
// tool cards. This is the main fix for the page seizing during long,
// multi-sandbox runs. Prior messages keep stable refs, so memo short-circuits.
const Message = memo(function Message({
  message,
  isStreaming,
}: {
  message: UIMessage;
  isLast: boolean;
  isStreaming: boolean;
}) {
  const isUser = message.role === "user";
  const parts = message.parts || [];

  // Deep-walking every tool output for citations is expensive — only recompute
  // when this message's parts change (not on every unrelated re-render).
  const citations = useMemo(
    () =>
      collectCitations(
        parts
          .filter((p) => p.type.startsWith("tool-"))
          .map((p) => (p as ToolPart).output),
      ),
    [parts],
  );

  return (
    <div
      data-testid={`message-${message.role}`}
      className={cn(
        "flex w-full animate-in fade-in slide-in-from-bottom-1 duration-200",
        isUser ? "justify-end" : "justify-start",
      )}
    >
      <div
        className={cn(
          "max-w-[85%] space-y-2 rounded-2xl px-4 py-2.5 text-sm break-words",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        {parts.map((p, i) => {
          if (p.type === "text") {
            const text = (p as { text?: string }).text || "";
            const isLastTextPart =
              i === parts.length - 1 ||
              !parts.slice(i + 1).some((q) => q.type === "text");
            if (isUser) {
              return (
                <div key={i} className="whitespace-pre-wrap leading-relaxed">
                  {text}
                </div>
              );
            }
            return (
              <div
                key={i}
                className="prose-message leading-relaxed [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-background/40 [&_pre]:p-2 [&_pre]:text-xs [&_code]:rounded [&_code]:bg-background/40 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px] [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_th]:border [&_th]:border-border [&_th]:bg-background/40 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_a]:text-emerald-600 [&_a]:underline-offset-2 [&_a:hover]:underline [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_strong]:font-semibold [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-2 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mt-2"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                {isStreaming && isLastTextPart && text && (
                  <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-foreground/60 align-text-bottom" />
                )}
              </div>
            );
          }
          if (p.type === "reasoning") {
            const reasoningText = ((p as { text?: string }).text || "").trim();
            if (!reasoningText) return null;
            const isLastPart = i === parts.length - 1;
            return (
              <ReasoningBlock
                key={i}
                text={reasoningText}
                active={isStreaming && isLastPart}
              />
            );
          }
          if (p.type.startsWith("tool-")) {
            return <ToolCallCard key={i} part={p as ToolPart} />;
          }
          if (p.type === "step-start") return null;
          return null;
        })}
        {!isUser && <SourcesStrip citations={citations} />}
      </div>
    </div>
  );
});
